import type { ListSource } from '../api/endpoints.ts';
import type { Category, StorefrontItem } from '../api/types.ts';
import { createStore, useStore } from '../core/store.ts';

/**
 * Навигация приложения: четыре вкладки, у каждой свой стек экранов, поверх них листы.
 * Кнопка «назад» браузера закрывает верхний слой — лист или экран стека.
 */
export type Tab = 'home' | 'catalog' | 'profile' | 'search';

export type Route =
  | { name: 'category'; category: Category }
  | { name: 'list'; source: ListSource; title: string }
  | { name: 'product'; item: StorefrontItem }
  | { name: 'notifications' }
  | { name: 'orders' }
  | { name: 'order'; orderId: string }
  | { name: 'addresses' }
  | { name: 'cards' }
  | { name: 'newAddress' }
  | { name: 'support'; orderId: string | null };

export type StackEntry = { key: number; route: Route };

/** Листы, которые поднимаются над любой вкладкой. Остальные листы экраны держат сами. */
export type GlobalSheet = 'cart' | 'signIn';

export type RouterState = {
  tab: Tab;
  /** Вкладка до текущей. */
  previousTab: Tab;
  stacks: Record<Tab, StackEntry[]>;
  sheets: GlobalSheet[];
};

export const routerStore = createStore<RouterState>({
  tab: 'home',
  previousTab: 'home',
  stacks: { home: [], catalog: [], profile: [], search: [] },
  sheets: [],
});

export function useRouter(): RouterState {
  return useStore(routerStore);
}

let nextKey = 1;

// Слои, закрываемые «назад» браузера. У каждого слоя своя запись в истории: число записей
// равно числу слоёв, поэтому «назад» всегда находит, что закрыть.
type Layer = { id: number; close: () => void };
const layers: Layer[] = [];

// Правки истории идут строго по очереди: `history.back()` выполняется не сразу, и запись,
// добавленная до его завершения, сбила бы счёт. Следующая правка ждёт `popstate` предыдущей.
const historyQueue: ('push' | 'back')[] = [];
let awaitingPop = false;
/** Сколько наших записей лежит в истории после выполненных правок. */
let historyDepth = 0;

function drainHistory(): void {
  while (!awaitingPop && historyQueue.length > 0) {
    if (historyQueue.shift() === 'push') {
      historyDepth += 1;
      history.pushState({ depth: historyDepth }, '');
    } else {
      historyDepth -= 1;
      awaitingPop = true;
      history.back();
    }
  }
}

function editHistory(edit: 'push' | 'back'): void {
  historyQueue.push(edit);
  drainHistory();
}

if (typeof window !== 'undefined') {
  history.replaceState({ depth: 0 }, '');
  window.addEventListener('popstate', (event) => {
    if (awaitingPop) {
      awaitingPop = false;
      drainHistory();
      return;
    }
    const depth = (event.state as { depth?: number } | null)?.depth ?? 0;
    if (depth > historyDepth) {
      // «Вперёд» после «назад»: закрытый слой не восстановить, запись возвращается обратно.
      awaitingPop = true;
      history.back();
      return;
    }
    historyDepth = depth;
    // «Назад» могло перескочить несколько записей: закрывается столько же слоёв.
    while (layers.length > historyDepth + historyQueue.filter((edit) => edit === 'push').length) layers.pop()?.close();
  });
}

/**
 * Слой, который закрывает «назад» браузера. Возвращает функцию снятия: её зовёт тот, кто
 * закрыл слой сам (кнопкой, жестом), чтобы запись истории ушла вместе с ним.
 */
export function pushLayer(close: () => void): () => void {
  const layer: Layer = { id: nextKey++, close };
  layers.push(layer);
  editHistory('push');
  return () => {
    const index = layers.indexOf(layer);
    if (index < 0) return;
    layers.splice(index, 1);
    editHistory('back');
  };
}

const releases = new Map<number, () => void>();
const sheetReleases = new Map<GlobalSheet, () => void>();

function removeEntry(tab: Tab, key: number): void {
  releases.delete(key);
  routerStore.set((state) => ({
    ...state,
    stacks: { ...state.stacks, [tab]: state.stacks[tab].filter((entry) => entry.key !== key) },
  }));
}

/** Экран поверх текущего стека вкладки. */
export function pushRoute(route: Route, tab: Tab = routerStore.get().tab): void {
  const key = nextKey++;
  routerStore.set((state) => ({ ...state, stacks: { ...state.stacks, [tab]: [...state.stacks[tab], { key, route }] } }));
  releases.set(
    key,
    pushLayer(() => removeEntry(tab, key)),
  );
}

/** Верхний экран стека текущей вкладки закрывается. */
export function popRoute(): void {
  const state = routerStore.get();
  const top = state.stacks[state.tab].at(-1);
  if (!top) return;
  const release = releases.get(top.key);
  removeEntry(state.tab, top.key);
  release?.();
}

/** Стек вкладки до корня. */
export function popToRoot(tab: Tab = routerStore.get().tab): void {
  for (const entry of [...routerStore.get().stacks[tab]].reverse()) {
    const release = releases.get(entry.key);
    removeEntry(tab, entry.key);
    release?.();
  }
}

/** Стек вкладки заменяется названными экранами: переход по уведомлению. */
export function setStack(tab: Tab, routes: Route[]): void {
  popToRoot(tab);
  selectTab(tab);
  for (const route of routes) pushRoute(route, tab);
}

/** Повторное нажатие на открытую вкладку возвращает её к корню. */
export function selectTab(tab: Tab): void {
  const state = routerStore.get();
  if (state.tab === tab) {
    popToRoot(tab);
    return;
  }
  routerStore.set({ ...state, tab, previousTab: state.tab });
}

export function openSheet(sheet: GlobalSheet): void {
  if (routerStore.get().sheets.includes(sheet)) return;
  routerStore.set((state) => ({ ...state, sheets: [...state.sheets, sheet] }));
  sheetReleases.set(
    sheet,
    pushLayer(() => {
      sheetReleases.delete(sheet);
      routerStore.set((state) => ({ ...state, sheets: state.sheets.filter((item) => item !== sheet) }));
    }),
  );
}

export function closeSheet(sheet: GlobalSheet): void {
  if (!routerStore.get().sheets.includes(sheet)) return;
  const release = sheetReleases.get(sheet);
  sheetReleases.delete(sheet);
  routerStore.set((state) => ({ ...state, sheets: state.sheets.filter((item) => item !== sheet) }));
  release?.();
}

export const openCart = () => openSheet('cart');
export const openSignIn = () => openSheet('signIn');

/** Заказ открывается из профиля поверх списка заказов; корзина при этом закрывается. */
export function openOrder(orderId: string): void {
  closeSheet('cart');
  setStack('profile', [{ name: 'orders' }, { name: 'order', orderId }]);
}

/** Заказ, экран которого открыт сейчас: полоса над вкладками и баннер о нём молчат. */
export function visibleOrderId(state: RouterState): string | null {
  const top = state.stacks[state.tab].at(-1)?.route;
  return top?.name === 'order' ? top.orderId : null;
}

/** Открыта карточка товара: у неё своя нижняя панель, вкладки и корзина на это время уходят. */
export function showsProductScreen(state: RouterState): boolean {
  return state.stacks[state.tab].at(-1)?.route.name === 'product';
}
